const SELECTOR = 'ghost-360-viewer';

class Ghost360Viewer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.image = this.querySelector('[data-ghost-360-image]');
    this.counter = this.querySelector('[data-ghost-360-counter]');
    this.stage = this.querySelector('.ghost-360-viewer__stage');
    this.frames = this.normalizeFrames(this.readFrames());
    this.index = 0;
    this.dragging = false;
    this.dragThreshold = 8;
    this.hasPassedDragThreshold = false;
    this.activePointerId = null;
    this.startX = 0;
    this.startIndex = 0;
    this.preloadedFrames = new Set();

    if (!this.image || !this.stage || this.frames.length === 0) return;

    this.showFrame(0);
    this.bindEvents();
    this.preloadFramesAfterPaint();
  }

  readFrames() {
    const source = this.querySelector('[data-ghost-360-frames]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  normalizeFrames(payload) {
    let frames = payload;

    if (typeof frames === 'string') {
      try {
        frames = JSON.parse(frames);
      } catch (error) {
        frames = [frames];
      }
    }

    if (!Array.isArray(frames)) return [];

    const seenSources = new Set();

    return frames
      .map((frame, index) => {
        if (typeof frame === 'string') {
          return {
            src: frame.trim(),
            alt: '',
            label: '',
            order: index,
          };
        }

        if (!frame || typeof frame !== 'object') return null;

        const image = frame.image;
        const previewImage = frame.preview_image;
        const src =
          frame.src ||
          frame.url ||
          (typeof image === 'string' ? image : image?.url || image?.src) ||
          (typeof previewImage === 'string' ? previewImage : previewImage?.url || previewImage?.src) ||
          '';

        return {
          src: typeof src === 'string' ? src.trim() : '',
          alt: frame.alt || frame.label || '',
          label: frame.label || '',
          order: Number.isFinite(Number(frame.order)) ? Number(frame.order) : index,
        };
      })
      .filter((frame) => {
        if (!this.isValidFrameSrc(frame?.src) || seenSources.has(frame.src)) return false;

        seenSources.add(frame.src);
        return true;
      })
      .sort((a, b) => a.order - b.order);
  }

  bindEvents() {
    this.stage.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
    this.stage.addEventListener('pointermove', (event) => this.handlePointerMove(event));
    this.stage.addEventListener('pointerup', (event) => this.endDrag(event));
    this.stage.addEventListener('pointercancel', (event) => this.endDrag(event));
    this.stage.addEventListener('pointerleave', (event) => this.handlePointerLeave(event));
    this.stage.addEventListener('lostpointercapture', (event) => this.endDrag(event));
    window.addEventListener('blur', () => this.endDrag());
  }

  handlePointerDown(event) {
    if (this.frames.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.dragging = true;
    this.hasPassedDragThreshold = false;
    this.activePointerId = event.pointerId;
    this.startX = event.clientX;
    this.startIndex = this.index;
    this.classList.add('is-dragging');

    try {
      this.stage.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Synthetic pointer events and older browsers can reject capture without affecting rotation.
    }
  }

  handlePointerMove(event) {
    if (this.frames.length <= 1) return;
    if (!this.dragging) return;
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;

    const delta = event.clientX - this.startX;
    const distance = Math.abs(delta);

    if (!this.hasPassedDragThreshold && distance < this.dragThreshold) {
      return;
    }

    this.hasPassedDragThreshold = true;

    const adjustedDelta = delta - Math.sign(delta) * this.dragThreshold;
    const frameStep = Math.max(16, this.stage.clientWidth / Math.max(this.frames.length, 4));
    const offset = Math.round(adjustedDelta / frameStep);

    this.showFrame(this.startIndex + offset);
    event.preventDefault();
  }

  endDrag(event) {
    if (!this.dragging) return;

    this.dragging = false;
    this.hasPassedDragThreshold = false;
    this.classList.remove('is-dragging');

    try {
      if (event?.pointerId !== undefined && this.stage.hasPointerCapture?.(event.pointerId)) {
        this.stage.releasePointerCapture(event.pointerId);
      }
    } catch (error) {
      // Capture may not exist for synthetic tests or canceled gestures.
    }

    this.activePointerId = null;
  }

  handlePointerLeave(event) {
    if (!this.dragging) return;

    this.endDrag(event);
  }

  showFrame(nextIndex) {
    const frameCount = this.frames.length;
    if (frameCount === 0) return;

    const normalizedIndex = ((nextIndex % frameCount) + frameCount) % frameCount;
    const frame = this.frames[normalizedIndex];
    if (!frame || !frame.src) return;

    this.index = normalizedIndex;

    if (this.image.getAttribute('src') !== frame.src) {
      this.image.src = frame.src;
    }

    if (frame.alt) {
      this.image.alt = frame.alt;
    }

    if (this.counter) {
      this.counter.textContent = `${normalizedIndex + 1} / ${frameCount}`;
    }

    this.preloadFrame(normalizedIndex + 1);
  }

  preloadFramesAfterPaint() {
    if (this.frames.length <= 1) return;

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        this.frames.forEach((_, index) => {
          if (index !== this.index) this.preloadFrame(index);
        });
      }, 0);
    });
  }

  preloadFrame(index) {
    if (this.frames.length <= 1) return;

    const normalizedIndex = ((index % this.frames.length) + this.frames.length) % this.frames.length;
    if (this.preloadedFrames.has(normalizedIndex)) return;

    const nextFrame = this.frames[normalizedIndex];
    if (!nextFrame?.src) return;

    const image = new Image();
    image.decoding = 'async';
    image.src = nextFrame.src;
    this.preloadedFrames.add(normalizedIndex);
  }

  isValidFrameSrc(src) {
    if (typeof src !== 'string') return false;

    return /^(https?:)?\/\//.test(src) || src.startsWith('/') || src.startsWith('data:image/');
  }
}

if (!customElements.get(SELECTOR)) {
  customElements.define(SELECTOR, Ghost360Viewer);
}

// Ghost 360 viewer initialization: upgrade any server-rendered viewers after the module loads.
document.querySelectorAll(SELECTOR).forEach((viewer) => {
  if (typeof viewer.connectedCallback === 'function') {
    viewer.connectedCallback();
  }
});
